import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { hasPartnerSignupProof, normalizeEmail } from '../utils/otpChallenge';

const PartnerSignupFormContext = createContext();
const draftKey = 'partnerSignupFormData';
const draftFields = ['email', 'phone', 'name', 'property_name', 'trade_license_number', 'unique_trade_number', 'vat_registration_number'];
const emptySecrets = {
  password: '', confirm_password: '', confirmPassword: '', otp: '', challenge_id: '',
  verification_token: '', verified_email: '', verification_expires_at: 0,
  step1Completed: false, step2Completed: false, step3Completed: false, terms_accepted: false,
};
const safeDraft = (data) => Object.fromEntries(draftFields.map(key => [key, typeof data?.[key] === 'string' ? data[key] : '']));

function loadDraft() {
  // The allowlisted, password-free signup draft is persisted in localStorage.
  let draft = safeDraft({});
  try {
    ['partner_verified_password', 'partner_verified_email', 'partner_email_verified', 'partner_toast_shown'].forEach(key => localStorage.removeItem(key));
    const saved = localStorage.getItem(draftKey);
    if (saved) draft = safeDraft(JSON.parse(saved));
  } catch {
    // Storage may be unavailable or malformed; never log its contents.
  }
  return { ...draft, ...emptySecrets };
}

export const usePartnerSignupForm = () => {
  const context = useContext(PartnerSignupFormContext);
  if (!context) {
    throw new Error('usePartnerSignupForm must be used within PartnerSignupFormProvider');
  }
  return context;
};

export const PartnerSignupFormProvider = ({ children }) => {
  const [formData, setFormData] = useState(loadDraft);

  useEffect(() => {
    try {
      localStorage.setItem(draftKey, JSON.stringify(safeDraft(formData)));
    } catch { /* Memory-only signup remains usable if storage is blocked. */ }
  }, [formData]);

  const updateFormData = useCallback((data) => {
    setFormData(prev => ({ ...prev, ...data,
      ...(data.email !== undefined && data.email !== prev.email ? emptySecrets : {}),
    }));
  }, []);

  const clearRegistrationSecrets = useCallback(() => {
    setFormData(prev => ({ ...prev, ...emptySecrets }));
  }, []);

  const setVerifiedRegistration = useCallback((grant, password) => {
    const verified = {
      email: normalizeEmail(grant?.email), verified_email: normalizeEmail(grant?.email),
      challenge_id: grant?.challenge_id, verification_token: grant?.verification_token,
      verification_expires_at: grant?.verification_expires_at,
      password, confirm_password: password,
    };
    if (!hasPartnerSignupProof(verified)) {
      clearRegistrationSecrets();
      return false;
    }
    setFormData(prev => ({ ...prev, ...emptySecrets, ...verified }));
    return true;
  }, [clearRegistrationSecrets]);

  const clearFormData = useCallback(() => {
    setFormData({ ...safeDraft({}), ...emptySecrets });
    try { localStorage.removeItem(draftKey); } catch { /* No persistent secrets. */ }
  }, []);

  const value = {
    formData,
    updateFormData,
    clearFormData,
    clearRegistrationSecrets,
    setVerifiedRegistration,
  };

  return (
    <PartnerSignupFormContext.Provider value={value}>
      {children}
    </PartnerSignupFormContext.Provider>
  );
};
